import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { DateTime } from "luxon";
import { Pencil, Flag, CircleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  useFacilitiesByUserId,
  useGetCurrentLocalFacilityId,
} from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import { useGetLeagueRosterQuery } from "../../../api/endpoints/roster";
import { useLeagueFacilityId } from "../../../hooks/useLeagueFacilityId";
import { leagueTypeLabel, scoringLabel, teeLabel, toRosterTeeType } from "../../helpers";
import { TeeBall } from "../../common/TeeBadge";
import {
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueWizardScheduleMode,
} from "../../../enums";
import { isTeamBasedFormat } from "../advancedLeagueSchema";
import { wizardLayout } from "../wizardLayout";
import type { AdvancedLeagueFormValues } from "../advancedLeagueSchema";

// The prior steps a Review card can jump back to. A subset of the editor's
// TabKey (Format/Season Setup/Location/Roster get a summary card).
export type ReviewStepKey = "structure" | "schedule" | "location" | "roster";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function fmtDate(value: string | undefined, withYear: boolean): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return "—";
  return dt.toFormat(withYear ? "MMM d, yyyy" : "MMM d");
}

function seasonRange(start?: string, end?: string): string {
  if (!start && !end) return "—";
  return `${fmtDate(start, false)} – ${fmtDate(end, true)}`;
}

function scheduleModeLabel(mode?: Clubhouse_LeagueWizardScheduleMode): string {
  if (mode === Clubhouse_LeagueWizardScheduleMode.WeeklyRounds)
    return "Fixed-time · Weekly";
  if (mode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex) return "Flexible";
  return "—";
}

function SummaryCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex items-center justify-between bg-muted/40 px-4 py-2.5">
        <h4 className="poppins text-sm font-semibold tracking-tight text-text-primary">
          {title}
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 rounded-md px-2 text-xs"
          onClick={onEdit}
        >
          <Pencil className="size-3" />
          Edit
        </Button>
      </div>
      <div className="px-4">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  isError,
}: {
  label: string;
  value: ReactNode;
  isError?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-0.5 border-b border-border py-2.5 last:border-0 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-baseline sm:gap-x-6">
      <span className="text-sm text-text-muted">{label}</span>
      <span
        className={cn(
          "text-sm font-medium",
          isError ? "text-destructive" : "text-text-primary",
        )}
      >
        {isError && (
          <CircleAlert
            aria-hidden
            className="mr-1.5 inline-block size-3.5 align-[-0.15em]"
          />
        )}
        {value}
      </span>
    </div>
  );
}

/**
 * The tee's name with its colour ball trailing, or the not-selected placeholder.
 * The ball sits after the name so the names share a left edge with every other
 * value in the column.
 */
function TeeRowValue({ tee }: { tee: number | null | undefined }) {
  const teeType = toRosterTeeType(tee);
  if (teeType == null) return <>Not selected</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      {teeLabel(teeType)}
      <TeeBall teeType={teeType} />
    </span>
  );
}

// Read-only recap, one card per prior step. The Finalize action lives in the
// editor footer; each card's Edit jumps back to that step (form state persists).
export function ReviewTab({
  leagueId,
  hasMultipleFacilities,
  rosterIssue,
  onEditStep,
}: {
  leagueId: string;
  hasMultipleFacilities: boolean;
  /**
   * What the roster would be rejected for, from the editor's own derivation so
   * the summary and the step badge cannot disagree. Undefined when it is sound.
   */
  rosterIssue?: string;
  onEditStep: (step: ReviewStepKey) => void;
}) {
  const { watch } = useFormContext<AdvancedLeagueFormValues>();
  const values = watch();

  // Scope the roster read to the league's own facility, not the sidebar one.
  const facilityId = useLeagueFacilityId(leagueId);
  const { data: rosterData } = useGetLeagueRosterQuery(
    { facilityId: facilityId ?? "", leagueId },
    { skip: !facilityId },
  );
  const members = (rosterData ?? []).filter((m) => m.isActive ?? true);
  const substitutes = members.filter((m) => m.isSubstituteOnly).length;
  const rosterStatus = { issue: rosterIssue };

  const leagueType = values.settings?.leagueType;
  const scoring = values.settings?.leagueScoringType;
  const showTeamMatchMode =
    isTeamBasedFormat(leagueType) && scoring === Clubhouse_LeagueScoring.MATCH;

  const isWeekly =
    values.scheduleMode === Clubhouse_LeagueWizardScheduleMode.WeeklyRounds;
  // `||` not `??`: on edit the form's seasonRounds is empty (rounds live in the
  // API) but seasonRoundCount is hydrated, and `0 ?? count` would wrongly stay 0.
  const roundCount =
    values.seasonRounds?.length || values.seasonRoundCount || 0;

  const homeFacilityId = useGetCurrentLocalFacilityId();
  const [userFacilities] = useFacilitiesByUserId(getUserIdFromToken());
  const facilities = userFacilities.filter(
    (f) => f.clubhouseFacilityId != null,
  );
  const selectedIds = values.participatingClubhouseFacilityIds ?? [];
  const selectedNames = facilities
    .filter((f) => selectedIds.includes(f.clubhouseFacilityId as number))
    .map((f) => f.name);
  // Name the facilities rather than counting them: "1 of 3" told the operator how
  // many they did not pick, which is not a fact about this league.
  const facilitiesValue = selectedNames.length
    ? selectedNames.join(", ")
    : values.facilityScope ===
        Clubhouse_LeagueFacilityScope.ANY_OF_MY_FACILITIES
      ? "Any of my facilities"
      : (facilities.find((f) => f.id === homeFacilityId)?.name ??
        "This facility only");
  const facilitiesLabel = selectedNames.length > 1 ? "Facilities" : "Facility";

  return (
    <div className={`${wizardLayout.section} flex flex-col gap-4`}>
      <SummaryCard title="Format" onEdit={() => onEditStep("structure")}>
        <Row
          label="League Format"
          value={leagueType != null ? leagueTypeLabel[leagueType] : "—"}
        />
        <Row
          label="Scoring Format"
          value={scoring != null ? scoringLabel[scoring] : "—"}
        />
        {showTeamMatchMode && (
          <Row label="Team Match Mode" value="Standard Team Match" />
        )}
      </SummaryCard>

      <SummaryCard title="Season Setup" onEdit={() => onEditStep("schedule")}>
        <Row
          label="Season Dates"
          value={seasonRange(values.leagueStartDate, values.leagueEndDate)}
        />
        <Row
          label="Schedule Mode"
          value={scheduleModeLabel(values.scheduleMode)}
        />
        {isWeekly && values.weekDay != null && (
          <Row label="Week Day" value={`Every ${WEEKDAYS[values.weekDay]}`} />
        )}
        <Row
          label="Men's Tee"
          value={<TeeRowValue tee={values.settings?.mensTee} />}
        />
        <Row
          label="Women's Tee"
          value={<TeeRowValue tee={values.settings?.womensTee} />}
        />
        <Row
          label="Rounds"
          value={`${roundCount} round${roundCount === 1 ? "" : "s"}`}
        />
      </SummaryCard>

      {hasMultipleFacilities && (
        <SummaryCard title="Location" onEdit={() => onEditStep("location")}>
          <Row label={facilitiesLabel} value={facilitiesValue} />
        </SummaryCard>
      )}

      <SummaryCard title="Roster" onEdit={() => onEditStep("roster")}>
        <Row
          label="Players"
          value={`${members.length} player${members.length === 1 ? "" : "s"}${
            substitutes > 0
              ? ` · ${substitutes} substitute${substitutes === 1 ? "" : "s"}`
              : ""
          }`}
        />
        <Row
          label="Roster Status"
          isError={!!rosterStatus.issue}
          value={rosterStatus.issue ?? "All players ready"}
        />
      </SummaryCard>

      <div className="flex gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Flag className="size-4" />
        </span>
        <p className="text-sm text-text-muted">
          <span className="font-semibold text-text-primary">
            Review and finalize.
          </span>{" "}
          Finalizing activates the league and locks its structure, scoring, and
          schedule — those can&apos;t be changed afterward. Other details stay
          editable from the league&apos;s own page.
        </p>
      </div>
    </div>
  );
}
